// frontend/src/components/ColumnConfigurator.jsx

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ColumnConfigurator.css';
import '../design-system.css';

function SortableItem({ id, col, handleColumnChange }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const itemClass = `column-item ${isDragging ? 'dragging' : ''}`;

  return (
    <div ref={setNodeRef} style={style} className={itemClass} {...attributes}>
      <div className="item-content">
        <span {...listeners} className="drag-handle">⠿</span>
        <input
          type="checkbox"
          checked={col.is_visible}
          onChange={(e) => handleColumnChange(id, 'is_visible', e.target.checked)}
        />
        <input
          type="text"
          value={col.display_alias}
          onChange={(e) => handleColumnChange(id, 'display_alias', e.target.value)}
          className="alias-input"
        />
        <div className="column-meta">
          <span className="original-name-tooltip">{col.original_name}</span>
          {col.context && col.context !== 'global' && (
            <span className="context-badge" title={`Contesto: ${col.context}`}>
              {col.context}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
export default function ColumnConfigurator({ show, onClose, initialConfig, onSave, isSaving, error }) {
  const [columns, setColumns] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // Stato ricerca

  // Fetch dynamic fields on mount
  useEffect(() => {
    if (show) {
      axios.get('/api/metadata/fields')
        .then(res => {
          const data = res.data;
          // Mappa i campi disponibili in oggetti colonna se non esistono già in initialConfig
          const existingNames = new Set(initialConfig.map(c => c.original_name.toLowerCase()));
          const newColumns = data.filter(field => !existingNames.has(field.toLowerCase())).map(field => ({
            context: 'global', // Default context
            original_name: field,
            display_alias: field.charAt(0).toUpperCase() + field.slice(1),
            is_visible: true,
            display_order: 100 // Append at end
          }));

          // Unisci config esistente con nuovi campi
          const merged = [...initialConfig, ...newColumns];

          // Raggruppa per original_name per gestire priorità contesti
          const byName = new Map();
          merged.forEach(col => {
            const name = col.original_name.toLowerCase();
            if (!byName.has(name)) byName.set(name, []);
            byName.get(name).push(col);
          });

          const unique = [];
          byName.forEach((cols) => {
            // Se ci sono colonne specifiche (es. aisarep), usa quelle e ignora la global
            const specifics = cols.filter(c => c.context && c.context !== 'global');
            if (specifics.length > 0) {
              unique.push(...specifics);
            } else {
              // Altrimenti usa la versione global (o l'unica disponibile)
              // Se ce ne sono multiple global (improbabile), ne prendiamo una
              unique.push(cols[0]);
            }
          });

          setColumns(unique.sort((a, b) => a.display_order - b.display_order));
        })
        .catch(err => console.error("Errore fetch campi metadata:", err));
    }
  }, [show, initialConfig]);


  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setColumns((items) => {
        // Trova indice basato su ID univoco (original_name non basta più se ci sono duplicati di nome)
        // MA SortableItem usa original_name come ID. Dobbiamo cambiare ID in SortableItem.
        // Per ora, assumiamo che ID sia original_name, ma questo romperà il drag & drop per colonne con stesso nome.
        // FIX: Usiamo una chiave univoca anche per l'ID del sortable.

        const oldIndex = items.findIndex((item) => item.original_name === active.id || `${item.original_name}|${item.context || 'global'}` === active.id);
        const newIndex = items.findIndex((item) => item.original_name === over.id || `${item.original_name}|${item.context || 'global'}` === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        return newOrder.map((col, index) => ({ ...col, display_order: index }));
      });
    }
  };

  const handleColumnChange = (id, field, value) => {
    // ID ora potrebbe essere composto
    setColumns(cols => cols.map(c => {
      const cId = `${c.original_name}|${c.context || 'global'}`;
      // Supporta sia ID vecchio (solo nome) che nuovo (composto) per retrocompatibilità
      const match = c.original_name === id || cId === id;
      return match ? { ...c, [field]: value } : c;
    }));
  };


  // Filtra colonne in base alla ricerca
  const filteredColumns = useMemo(() => {
    if (!searchTerm) return columns;
    const lowerTerm = searchTerm.toLowerCase();
    return columns.filter(c =>
      c.original_name.toLowerCase().includes(lowerTerm) ||
      c.display_alias.toLowerCase().includes(lowerTerm)
    );
  }, [columns, searchTerm]);

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content column-config-modal">
        <div className="modal-header">
          <h3>Gestisci Colonne Tabella</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          <p className="help-text">Trascina per ordinare, spunta per mostrare/nascondere.</p>

          {/* Barra di ricerca */}
          <input
            type="text"
            placeholder="Cerca colonna..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ds-input"
            style={{ marginBottom: '12px' }}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredColumns.map(c => `${c.original_name}|${c.context || 'global'}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="columns-list">
                {filteredColumns.map((col) => {
                  const uniqueId = `${col.original_name}|${col.context || 'global'}`;
                  return (
                    <SortableItem
                      key={uniqueId}
                      id={uniqueId}
                      col={col}
                      handleColumnChange={handleColumnChange}
                    />
                  );
                })}
                {filteredColumns.length === 0 && <p style={{ textAlign: 'center', color: 'var(--ds-txt-2)' }}>Nessuna colonna trovata.</p>}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Annulla</button>
          <button className="save-btn" onClick={() => onSave(columns)} disabled={isSaving}>
            {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
}
